/**
 * 从 `collectedFields`（SaveGoal 字段袋）解析聚合弹窗所需开关与展示字段。
 *
 * 不负责 confirm-save / goalId；调用方在打开弹窗前自行处理持久化逻辑。
 */

/** 解析后的结构化入参，供分支判断与 UI 映射使用。 */
export type AggregatedOnboardingParsedInput = {
  /** `true` → 跳过 ⑥⑦，直接 ⑧ */
  displayGoalTrackerListDirectly: boolean;
  /** `true` 且非 direct → 需要 ⑥ Loading + POST /onboarding/generate */
  hasBoundPortfolio: boolean;
  /** 存在则 ⑧ 显示 Save this setup 并在点击后调用；否则显示 Confirm 仅关窗 */
  handleSubmitGoal?: () => void | Promise<void>;
  title?: string;
  summary?: string;
  trackerSpecs?: unknown;
};

/**
 * 将 `openAggregatedOnboardingModal({ collectedFields })` 的原始对象转为强类型视图。
 * 缺失字段使用安全默认值，避免运行时抛错。
 */
export const parseAggregatedOnboardingInput = (
  raw: Record<string, unknown> | undefined,
): AggregatedOnboardingParsedInput => {
  if (!raw || typeof raw !== "object") {
    return {
      displayGoalTrackerListDirectly: false,
      hasBoundPortfolio: false,
    };
  }

  const handleSubmitGoal =
    typeof raw.handleSubmitGoal === "function"
      ? (raw.handleSubmitGoal as () => void | Promise<void>)
      : undefined;

  return {
    displayGoalTrackerListDirectly: Boolean(raw.displayGoalTrackerListDirectly),
    hasBoundPortfolio: Boolean(raw.hasBoundPortfolio),
    handleSubmitGoal,
    title: typeof raw.title === "string" ? raw.title : undefined,
    summary: typeof raw.summary === "string" ? raw.summary : undefined,
    trackerSpecs: raw.trackerSpecs,
  };
};

/**
 * 计算打开 Modal 时的**首屏**步骤（尚未请求 generate）。
 *
 * | displayGoalTrackerListDirectly | hasBoundPortfolio | 首屏 |
 * |-------------------------------|-------------------|------|
 * | true | * | trackers（⑧） |
 * | false | false | trackers（⑧） |
 * | false | true | loading（⑥） |
 */
export const resolveInitialAggregatedStep = (
  input: AggregatedOnboardingParsedInput,
): "loading" | "trackers" => {
  if (input.displayGoalTrackerListDirectly) {
    return "trackers";
  }
  if (!input.hasBoundPortfolio) {
    return "trackers";
  }
  return "loading";
};

/**
 * 是否应在 ⑥ 阶段调用 `POST /onboarding/generate`。
 * 与 PRD 一致：仅「已绑定持仓且非直接进结果页」时生成诊断卡片。
 */
export const shouldRunOnboardingGenerate = (
  input: AggregatedOnboardingParsedInput,
): boolean => {
  return !input.displayGoalTrackerListDirectly && input.hasBoundPortfolio;
};
